import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[300] bg-background/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/**
 * `grid-cols-[minmax(0,1fr)]` on the Content and `sm:flex-wrap` on the Footer
 * are load-bearing, not cosmetic, and they only work as a PAIR.
 *
 * Without an explicit template the single implicit column is
 * `grid-auto-columns: auto`, whose MINIMUM is the largest min-content
 * contribution of its items — a floor that `max-w-*` cannot bound. One child
 * with `truncate` (which implies `white-space: nowrap`, so min-content ==
 * max-content) blows the dialog open: measured at 899px of content inside a
 * 576px box, clipping the right-hand edge off screen. `min-w-0` on the
 * offending child does not help, because the floor is applied at the
 * grid-ITEM level (CSS Grid §6.6).
 *
 * But removing that floor alone makes things WORSE for the footer. This
 * element is its own horizontal scroller (`overflow-y-auto` with an unset
 * `overflow-x` computes overflow-x to `auto`), so today an over-wide footer
 * scrolls and stays reachable. Squeeze it to the box width and, because the
 * row is `justify-end` and the buttons are `whitespace-nowrap shrink-0`, it
 * overflows the INLINE-START edge — where the scrollable region does not
 * extend in LTR. Measured: maxScrollLeft 0, first button at -144.5px, i.e.
 * clipped and unclickable with no scrollbar to hint at it. `sm:flex-wrap`
 * turns that into a second row instead. Do not remove one without the other.
 *
 * Both are inert for dialogs that already fit: an `auto` track in a
 * definite-width grid container already absorbs free space like `1fr` under
 * the default `justify-content` (§12.8), and a footer that fits never wraps.
 * A consumer needing a different template can still pass its own
 * `grid-cols-*` — twMerge lets the later class win.
 *
 * Still NOT handled here, because it is not safe globally: this element is
 * also the VERTICAL scroller for the whole box, so the footer scrolls away on
 * a long dialog. A consumer that needs it pinned adds
 * `grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden` here and
 * `min-h-0 overflow-y-auto` on its own body element.
 */
const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-[300] grid grid-cols-[minmax(0,1fr)] w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-4 sm:p-6 shadow-lg duration-200 max-h-[85vh] overflow-y-auto data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:flex-wrap sm:justify-end sm:gap-y-2 sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
